import { type Dispatch, type FC, type SetStateAction } from "react";
import { Cassette } from "@root/shared/components/Cassette";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { Accordion } from "@root/shared/components/Accordion";
import { Button } from "@root/shared/components/Button";
import type {
  PhotoContestEntry,
  VoteButtonText,
} from "@clientFrontend/features/campaign/components/UserPhotoContest/type";
import { Icon } from "@root/shared/components/Icon";
import { TextLink } from "@root/shared/components/TextLink";
import * as styles from "./styles.css";
import { CommentModal } from "./uiParts/CommentModal";

type Props = {
  entryNo: number;
  isEntryVoted: boolean;
  isAccordionOpened: boolean;
  isClientScoutable: boolean;
  photoContestEntry: Omit<PhotoContestEntry, "isEntryVoted">;
  isClientNotVotable: boolean;
  isVotingPeriodOver: boolean;
  voteButtonText: VoteButtonText;
  toggleAccordion: () => void;
  handleVoteContestEntry: () => void;
  isCommentModalOpen: boolean;
  setIsCommentModalOpen: Dispatch<SetStateAction<boolean>>;
  comment: string;
  setComment: Dispatch<SetStateAction<string>>;
  handleSubmitComment: () => void;
  isCommentSubmitting: boolean;
};

export const Presentation: FC<Props> = ({
  entryNo,
  isEntryVoted,
  isAccordionOpened,
  isClientScoutable,
  photoContestEntry,
  isClientNotVotable,
  isVotingPeriodOver,
  voteButtonText,
  toggleAccordion,
  handleVoteContestEntry,
  isCommentModalOpen,
  setIsCommentModalOpen,
  comment,
  setComment,
  handleSubmitComment,
  isCommentSubmitting,
}) => {
  const isNotVotable = isEntryVoted || isClientNotVotable || isVotingPeriodOver;

  return (
    <Cassette className={styles.cassetteWrapper}>
      <LazyLoadImage
        src={photoContestEntry.imageUrl}
        alt={`${photoContestEntry.nickname}のフォトコンテストエントリー画像`}
        width={284}
        height={377}
      />
      <div className={styles.entryNumber}>No.{String(entryNo).padStart(3, "0")}</div>
      <div className={styles.entryDescriptionContainer}>
        <div className={styles.entryTitleThemeWrapper}>
          <div className={styles.entryTheme}>{photoContestEntry.theme}</div>
          <div className={styles.fontWeightBold}>『{photoContestEntry.title}』</div>
        </div>
        <div className={styles.descriptionAccordionContainer}>
          <Accordion
            title="学校（学年）・作品への想い"
            isOpened={isAccordionOpened}
            accordionTheme="panel"
            onClick={toggleAccordion}
          >
            <div>
              <p>
                {photoContestEntry.schoolName} ({photoContestEntry.schoolGrade}年生)
              </p>
              <p className={styles.descriptionTitle}>作品への想い</p>
              <p className={styles.description}>{photoContestEntry.description}</p>
            </div>
          </Accordion>
        </div>
        <div className={styles.voteButton}>
          <Button
            size="large"
            theme={isNotVotable ? "disabled" : "primary"}
            onClick={handleVoteContestEntry}
          >
            {voteButtonText}
          </Button>
        </div>
        {isClientScoutable && (
          <div className={styles.scoutLinks}>
            <TextLink
              href={photoContestEntry.scoutUrl}
              target="_blank"
              className="ga-click"
              data-ga-label={entryNo}
              data-ga-action={`photocon_scout_button_${photoContestEntry.division}`}
            >
              <div className={styles.scoutLinkItems}>
                <Icon fontSize="16px" name="scout" />
                スカウト
              </div>
            </TextLink>
            <span className={styles.scoutLinksDivider} />
            <TextLink
              href={photoContestEntry.premiumScoutUrl}
              target="_blank"
              className="ga-click"
              data-ga-label={entryNo}
              data-ga-action={`photocon_premium_scout_button_${photoContestEntry.division}`}
            >
              <div className={styles.scoutLinkItems}>
                <Icon fontSize="16px" name="scout" />
                プレミアムスカウト
              </div>
            </TextLink>
          </div>
        )}
      </div>
      <CommentModal
        isOpen={isCommentModalOpen}
        onClose={() => setIsCommentModalOpen(false)}
        comment={comment}
        onCommentChange={(value) => setComment(value)}
        onSubmit={handleSubmitComment}
        isSubmitting={isCommentSubmitting}
      />
    </Cassette>
  );
};
